import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Menu } from 'lucide-react';

import { cn } from '@/lib/cn';
import { BrandWordmark } from './sidebar';
import { TopBarIconButton } from './top-bar';

/**
 * Phone header: glass bar with the menu button, the brand and the same icon
 * buttons as the desktop top bar. `AppShell` shows it below `lg` only.
 */
export function MobileTopBar({
  onMenu,
  actions,
  className,
}: {
  /** Opens the `MobileDrawer`. */
  onMenu: () => void;
  /** Right side: theme toggle, bell, help. */
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <header data-slot="mobile-top-bar" className={cn('sticky top-3 z-20 mx-3 mt-3 flex h-[52px] shrink-0 items-center gap-1 rounded-card px-1.5', className)}>
      <TopBarIconButton label="Abrir el menú" onClick={onMenu} className="relative">
        <Menu />
      </TopBarIconButton>
      <BrandWordmark className="relative ml-1" />
      <div className="relative ml-auto flex items-center">{actions}</div>
    </header>
  );
}

export interface MobileDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The sidebar content: the very same `Sidebar*` pieces. */
  children: React.ReactNode;
  /** Portal target; give it a positioned element to keep the drawer inside it (catalog phone frame). */
  container?: HTMLElement | null;
  className?: string;
}

/**
 * Left drawer with the sidebar content, opened by the ☰ button: black glass, a
 * scrim and a slide-in. Built on the Dialog primitive, so it traps focus and
 * closes on Esc or a tap on the scrim.
 */
export function MobileDrawer({ open, onOpenChange, children, container, className }: MobileDrawerProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal container={container ?? undefined}>
        <DialogPrimitive.Overlay
          data-slot="drawer-overlay"
          className={cn(container ? 'absolute' : 'fixed', 'inset-0 z-dialog bg-overlay')}
        />
        <DialogPrimitive.Content
          data-slot="mobile-drawer"
          aria-describedby={undefined}
          className={cn(
            container ? 'absolute' : 'fixed',
            'inset-y-0 left-0 z-dialog-content flex w-[min(312px,calc(100%-3rem))] flex-col gap-1 px-3 pb-[max(1.125rem,env(safe-area-inset-bottom))] pt-[max(1.125rem,env(safe-area-inset-top))] text-ink outline-none',
            className,
          )}
        >
          <DialogPrimitive.Title className="sr-only">Menú</DialogPrimitive.Title>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
